import React, { useEffect, useMemo, useState } from "react";
import "./Dashboard.css";
import axiosInstance from "./axiosinstance";
import SearchableDropdown from "./Components/SearchableDropdown";
import {
  FiCheckCircle,
  FiFileText,
  FiUsers,
  FiDollarSign,
  FiClock,
} from "react-icons/fi";


/* ---------- helpers ---------- */
const isNum = (v) => typeof v === "number" && !Number.isNaN(v);
const hasNumber = (v) =>
  v !== null && v !== undefined && !Number.isNaN(Number(v));

const formatCurrency = (value) =>
  hasNumber(value)
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value))
    : "N/A";

const formatNumber = (value) =>
  hasNumber(value)
    ? new Intl.NumberFormat("en-IN").format(Number(value))
    : "N/A";

const QUICK_PERIOD_OPTIONS = [
  { value: "last_7_days", label: "Last 7 days", type: "quick" },
  { value: "last_30_days", label: "Last 30 days", type: "quick" },
  { value: "last_90_days", label: "Last 90 days", type: "quick" },
  { value: "last_6_months", label: "Last 6 months", type: "quick" },
  { value: "last_year", label: "Last year", type: "quick" },
  { value: "custom", label: "Custom range", type: "custom" },
];

const FALLBACK_PERIOD_OPTION =
  QUICK_PERIOD_OPTIONS.find((option) => option.value === "last_30_days") ||
  QUICK_PERIOD_OPTIONS[0];

function normalizeDateInput(value) {
  let normalizedValue = value;
  if (normalizedValue) {
    const [y, m, d] = normalizedValue.split("-");
    if (y && y.length > 4) {
      normalizedValue = `${y.slice(0, 4)}-${m || "01"}-${d || "01"}`;
    }
  }
  return normalizedValue;
}

function buildDashboardKpiParams({
  selectedSubsidiary,
  selectedPeriodOption,
  customStartDate,
  customEndDate,
}) {
  const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;
  const params = {
    subsidiary_selected: selectedSubsidiary || "all",
  };

  if (selectedOption.type === "fy") {
    if (selectedOption.fy_start) params.fy_start = selectedOption.fy_start;
    if (selectedOption.fy_end) params.fy_end = selectedOption.fy_end;
    return params;
  }

  if (selectedOption.type === "custom") {
    params.filter_type = "custom";
    if (customStartDate) params.start_date = customStartDate;
    if (customEndDate) params.end_date = customEndDate;
    return params;
  }

  params.filter_type = selectedOption.value;
  return params;
}

function aggregateAmountAndCount(data, amountKey, countKey) {
  if (Array.isArray(data)) {
    return data.reduce(
      (acc, item) => ({
        amount: acc.amount + (Number(item?.[amountKey]) || 0),
        count: acc.count + (Number(item?.[countKey]) || 0),
      }),
      { amount: 0, count: 0 },
    );
  }

  if (data && typeof data === "object") {
    return {
      amount: Number(data[amountKey]) || 0,
      count: Number(data[countKey]) || 0,
    };
  }

  return { amount: 0, count: 0 };
}

/* -------------------- KPI Card -------------------- */
function KPICard({
  title,
  count,
  amount,
  percentage,
  icon,
  toneClass = "",
  showOnlyAmount,
  showOnlyCount,
  showConversion,
}) {
  const displayAmount = formatCurrency(amount);
  const displayCount = formatNumber(count);
  const conversionText = hasNumber(percentage)
    ? `${percentage}% conversion rate`
    : "N/A conversion rate";
  const hasFootnote = showConversion || (!showOnlyAmount && !showOnlyCount);

  return (
    <div
      className={`card kpi-card ${toneClass} ${
        hasFootnote ? "kpi-card-has-footnote" : ""
      }`}
    >
      <div className="kpi-card-inner">
        <div className="kpi-icon-circle">{icon}</div>
        <div className="kpi-copy">
          <div className="card-title">{title}</div>
          {showOnlyAmount && <div className="kpi-big">{displayAmount}</div>}

          {showOnlyCount && <div className="kpi-big">{displayCount}</div>}

          {showConversion && (
            <>
              <div className="kpi-big">{displayCount}</div>
              <div className="kpi-subtext">{conversionText}</div>
            </>
          )}

          {!showOnlyAmount && !showOnlyCount && !showConversion && (
            <>
              <div className="kpi-big">{displayAmount}</div>
              <div className="kpi-line">
                {hasNumber(count) ? `${formatNumber(count)} invoices` : "N/A"}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Services Table -------------------- */
function ServicesChart({ services = [] }) {
  // Add sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // Sorting function
  const sortedServices = useMemo(() => {
    if (!sortConfig.key) return services;

    return [...services].sort((a, b) => {
      if (sortConfig.key === "service_name") {
        const aVal = (a.service_name || "").toLowerCase();
        const bVal = (b.service_name || "").toLowerCase();
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (sortConfig.key === "revenue") {
        const aVal = Number(a.total_money_earned_with_service) || 0;
        const bVal = Number(b.total_money_earned_with_service) || 0;
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (sortConfig.key === "usage") {
        const aVal = Number(a.usage_count) || 0;
        const bVal = Number(b.usage_count) || 0;
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [services, sortConfig]);

  // Handle sort click
  const requestSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Get sort direction indicator
  const getSortDirection = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  return (
    <div className="service-chart-dashboard">
      <div className="">
        <div className="card-header card-header-row">
          <div>
            <div className="card-title-lg">Top 5 Most Performing Services</div>
            <div className="muted">
              Revenue and usage statistics for your key services
            </div>
          </div>
        </div>

        <div className="card-content">
          <div className="table header mb0">
            <div
              className=" table-head clickable"
              onClick={() => requestSort("service_name")}
            >
              Service Name{" "}
              <span className="sort-icon">
                {getSortDirection("service_name")}
              </span>
            </div>
            <div
              className="table-head clickable"
              onClick={() => requestSort("revenue")}
            >
              Total Revenue (₹){" "}
              <span className="sort-icon">{getSortDirection("revenue")}</span>
            </div>
            <div
              className="table-head clickable"
              onClick={() => requestSort("usage")}
            >
              Usage Count{" "}
              <span className="sort-icon">{getSortDirection("usage")}</span>
            </div>
          </div>

          {sortedServices.map((s, idx) => (
            <div key={s.service_name || idx} className="table row">
              <div className="td service-cell">
                <div className="rank">{idx + 1}</div>
                <span className="service-name">{s.service_name || "N/A"}</span>
              </div>
              <div className="td">
                {formatCurrency(s.total_money_earned_with_service)}
              </div>
              <div className="td">{formatNumber(s.usage_count)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Dashboard -------------------- */
const Dashboard = ({ isSidebarExpanded }) => {
  // Subsidiary select
  const [selectedSubsidiary, setSelectedSubsidiary] = useState("all");
  const [subsidiaries, setSubsidiaries] = useState([]);

  // Unified period options (FY + quick + custom)
  const [dashboardPeriodOptions, setDashboardPeriodOptions] =
    useState(QUICK_PERIOD_OPTIONS);
  const [selectedPeriodOption, setSelectedPeriodOption] = useState(
    FALLBACK_PERIOD_OPTION,
  );
  const [hasLoadedPeriodOptions, setHasLoadedPeriodOptions] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Server data
  const [billingData, setBillingData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [outstanding, setOutstanding] = useState(0);
  const [topServices, setTopServices] = useState([]);

  /* Load subsidiaries (Active) */
  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const res = await axiosInstance.get(`/api/get_all_subsidiary`, {
          params: { page_status: "Active" },
        });

        const raw = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
        console.debug("subsidiary raw:", res.data);

        const list = raw
          .map((x) => ({
            subsidiary_id: x.subsidiary_id,
            subsidiary_name: x.Subsidiary_name, // per sample
          }))
          .filter((x) => x.subsidiary_id && x.subsidiary_name);

        if (list.length > 0) {
  const withAll = [
    { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
    ...list,
  ];
  setSubsidiaries(withAll);
  if (!withAll.some((s) => s.subsidiary_id === selectedSubsidiary)) {
          setSelectedSubsidiary("all");
        }
} else {
  setSubsidiaries([]); // no options at all
}
        
      } catch (e) {
        console.error("Error fetching subsidiaries:", e);
        setSubsidiaries([
          // { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
        ]);
        // if (selectedSubsidiary !== "all") setSelectedSubsidiary("all");
      }
    };
    fetchSubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const response = await axiosInstance.get("/api/financial-years");
        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const fyOptions = list.map((financialYear) => ({
          label: `FY ${financialYear.fy_label}`,
          value: `fy_${financialYear.id}`,
          type: "fy",
          id: financialYear.id,
          fy_label: financialYear.fy_label,
          fy_start: financialYear.start_date,
          fy_end: financialYear.end_date,
          is_active: financialYear.is_active,
        }));

        const mergedOptions = [...fyOptions, ...QUICK_PERIOD_OPTIONS];
        const defaultSelectedOption =
          fyOptions.find((option) => option.is_active) ||
          fyOptions[0] ||
          FALLBACK_PERIOD_OPTION;

        setDashboardPeriodOptions(mergedOptions);
        setSelectedPeriodOption(defaultSelectedOption);
      } catch (e) {
        console.error("Error fetching financial years:", e);
        setDashboardPeriodOptions(QUICK_PERIOD_OPTIONS);
        setSelectedPeriodOption(FALLBACK_PERIOD_OPTION);
      } finally {
        setHasLoadedPeriodOptions(true);
      }
    };

    fetchFinancialYears();
  }, []);

  /* Fetch KPI/services whenever period/subsidiary/custom dates change */
  useEffect(() => {
    if (!hasLoadedPeriodOptions) return;

    const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;
    if (selectedOption.type === "custom") {
      if (!customStartDate || !customEndDate) return;
      if (new Date(customStartDate) > new Date(customEndDate)) {
        setErr("Start Date cannot be after End Date.");
        return;
      }
    }

    const fetchKPI = async () => {
      setLoading(true);
      setErr("");
      try {
        const params = buildDashboardKpiParams({
          selectedSubsidiary,
          selectedPeriodOption: selectedOption,
          customStartDate,
          customEndDate,
        });

        const res = await axiosInstance.get(`/api/dashboard/kpi`, { params });
        const json = res.data || {};

        setBillingData(json.billing_data || null);
        setPaymentData(json.payment_data || null);
        setVisitors(json.total_visitor || null);
        setOutstanding(
          isNum(json.total_outstanding) ? json.total_outstanding : null,
        );
        setTopServices(
          Array.isArray(json.top_services) ? json.top_services : [],
        );
      } catch (e) {
        console.error(e);
        setErr("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };
    fetchKPI();
  }, [
    selectedSubsidiary,
    selectedPeriodOption,
    customStartDate,
    customEndDate,
    hasLoadedPeriodOptions,
  ]);

  const handlePeriodChange = (nextSelectedOption) => {
    const selectedOption = nextSelectedOption || FALLBACK_PERIOD_OPTION;

    setSelectedPeriodOption(selectedOption);
    setErr("");

    if (selectedOption.type !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  /* KPIs derived props */
  const kpis = useMemo(() => {
    const billingSummary = aggregateAmountAndCount(
      billingData,
      "total_billing",
      "invoice_count",
    );
    const paymentSummary = aggregateAmountAndCount(
      paymentData,
      "total_received",
      "payment_count",
    );
    const totalVisitors = visitors?.total_visitors;
    const converted = visitors?.conversion_visitors;
    const conversionRate = visitors?.conversion_rate;
    const totalOutstanding = hasNumber(outstanding)
      ? outstanding
      : billingSummary.amount - paymentSummary.amount;

    return {
      totalInvoices: {
        count: billingSummary.count,
        amount: billingSummary.amount,
      },
      payments: { count: paymentSummary.count, amount: paymentSummary.amount },
      outstanding: totalOutstanding,
      visitors: { count: totalVisitors },
      converted: { count: converted, percentage: conversionRate },
    };
  }, [billingData, paymentData, visitors, outstanding]);

  return (
    <div
      className={`dashboard-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="main">
        <div className="table-header dashboard-page-header">
          <div className="table-header-content dashboard-page-header-content">
            {/* LEFT SIDE */}
            <div className="table-left-side dashboard-title-block">
              <h2 className="jost-bold">Dashboard Overview</h2>
              <p className="dashboard-subtitle">
                Track invoices, payments, visitors and service performance in one place.
              </p>
            </div>

            {/* RIGHT SIDE */}
            <div className="table-right-side dashboard-filter-row">
              {/* Subsidiary Select */}
              <div className="entity-select-dashboard">
                {subsidiaries.length > 0 ? (
                  <SearchableDropdown
                    className="dashboard-filter-dropdown"
                    triggerClassName="select entity-select"
                    value={selectedSubsidiary}
                    onChange={(option) =>
                      setSelectedSubsidiary(option?.subsidiary_id || "all")
                    }
                    options={subsidiaries}
                    getOptionValue={(option) => option.subsidiary_id}
                    getOptionLabel={(option) => option.subsidiary_name}
                    placeholder="Select Subsidiary"
                    searchPlaceholder="Search subsidiary"
                  />
                ) : (
                  <div className="no-subsidiary-text">
                    No subsidiaries found
                  </div>
                )}
              </div>

              {/* Date Range */}
              <div className="date-picker">
                <div className="date-range">
                  <SearchableDropdown
                    className="dashboard-filter-dropdown"
                    triggerClassName="select range-select"
                    value={selectedPeriodOption?.value || ""}
                    onChange={handlePeriodChange}
                    options={dashboardPeriodOptions}
                    getOptionValue={(option) => option.value}
                    getOptionLabel={(option) => option.label}
                    placeholder="Select Dashboard Period"
                    searchPlaceholder="Search period"
                    disabled={dashboardPeriodOptions.length === 0}
                  />

                  {selectedPeriodOption?.type === "custom" && (
                    <div className="custom-inline">
                      <div className="date-field sm">
                        <label className="label-sm">Start Date</label>
                        <input
                          type="date"
                          className="input-date"
                          value={customStartDate}
                          onChange={(e) => {
                            setCustomStartDate(
                              normalizeDateInput(e.target.value),
                            );
                            setErr("");
                          }}
                          max="2100-12-31"
                          min="1900-01-01"
                        />
                      </div>

                      <div className="date-field sm">
                        <label className="label-sm">End Date</label>
                        <input
                          type="date"
                          className="input-date"
                          value={customEndDate}
                          onChange={(e) => {
                            setCustomEndDate(
                              normalizeDateInput(e.target.value),
                            );
                            setErr("");
                          }}
                          max="2100-12-31"
                          min="1900-01-01"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="dashboard-header">
          <h1 className="dashboard-title">Dashboard Overview</h1>

          <div className="dashboard-filters">
            <div className="entity-select-dashboard">
              <select
                className="select entity-select"
                aria-label="Select Subsidiary"
                value={selectedSubsidiary}
                onChange={(e) => setSelectedSubsidiary(e.target.value)}
              >
                <option value="" disabled>
                  Select Subsidiary
                </option>
                {subsidiaries.map((s) => (
                  <option
                    key={s.subsidiary_id}
                    value={s.subsidiary_id}
                    style={{
                      border: "1px solid #000",
                      backgroundColor: "#f3f3f3ff",
                      zIndex: "999",
                      top: "",
                    }}
                  >
                    {s.subsidiary_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="date-picker">
              <div className="date-range">
                <select
                  className="select range-select"
                  aria-label="Select Dashboard Period"
                  value={selectedPeriodOption?.value || ""}
                  onChange={handlePeriodChange}
                >
                  {hasFinancialYears && financialYearOptions.length > 0 && (
                    <optgroup label="Financial Years">
                      {financialYearOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Quick Filters">
                    {quickPeriodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                </select>

                {selectedPeriodOption?.type === "custom" && (
                  <div className="custom-inline">
                    <div className="date-field sm">
                      <label className="label-sm">Start Date</label>
                      <input
                        type="date"
                        className="input-date"
                        value={customStartDate}
                        onChange={(e) => {
                          setCustomStartDate(
                            normalizeDateInput(e.target.value),
                          );
                          setErr("");
                        }}
                        max="2100-12-31"
                        min="1900-01-01"
                      />
                    </div>
                    <div className="date-field sm">
                      <label className="label-sm">End Date</label>
                      <input
                        type="date"
                        className="input-date"
                        value={customEndDate}
                        onChange={(e) => {
                          setCustomEndDate(normalizeDateInput(e.target.value));
                          setErr("");
                        }}
                        max="2100-12-31"
                        min="1900-01-01"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div> */}

        {err && (
          <div className="muted" style={{ color: "#b42318" }}>
            {err}
          </div>
        )}
        {loading && <div className="muted">Loading…</div>}

        {!loading && (
          <div className="kpi-grid">
            <KPICard
              title="Total Invoices"
              count={kpis.totalInvoices.count}
              amount={kpis.totalInvoices.amount}
              icon={<FiFileText />}
              toneClass="kpi-card-emerald"
            />
            <KPICard
              title="Payments Received"
              count={kpis.payments.count}
              amount={kpis.payments.amount}
              // icon={<FiCreditCard />}
              icon={<FiDollarSign />}
              toneClass="kpi-card-blue"
            />
            <KPICard
              title="Outstanding Amount"
              amount={kpis.outstanding}
              // icon={<FiDollarSign />}
              icon={<FiClock />}
              toneClass="kpi-card-amber"
              showOnlyAmount
            />
            <KPICard
              title="Total Visitors"
              count={kpis.visitors.count}
              icon={<FiUsers />}
              toneClass="kpi-card-slate"
              showOnlyCount
            />
            <KPICard
              title="Converted Visitors"
              count={kpis.converted.count}
              percentage={kpis.converted.percentage}
              icon={<FiCheckCircle />}
              toneClass="kpi-card-rose"
              showConversion
            />
          </div>
        )}

        {!loading && <ServicesChart services={topServices} />}
      </div>
    </div>
  );
};

export default Dashboard;
